import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";

const GoogleBtn = () => {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={go} data-testid="google-login-btn"
      className="w-full h-12 rounded-full border-brand/20 gap-2.5 text-brand hover:bg-brand-surface">
      <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
      Continue with Google
    </Button>
  );
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo /></div>
        <div className="bg-white border border-brand/10 rounded-sm p-8 shadow-sm">
          <h1 className="font-display text-3xl text-brand text-center">Welcome back</h1>
          <p className="text-sm text-brand-muted text-center mt-1">Sign in to track your orders</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <Label htmlFor="email" className="text-brand">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12 rounded-sm" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-brand">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12 rounded-sm" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-brand-terracotta" data-testid="login-error">{error}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-12 bg-brand hover:bg-brand-hover text-white rounded-full">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-5"><div className="h-px flex-1 bg-brand/10" /><span className="text-xs text-brand-muted">OR</span><div className="h-px flex-1 bg-brand/10" /></div>
          <GoogleBtn />

          <p className="text-sm text-brand-muted text-center mt-6">
            New here? <Link to="/register" data-testid="go-register" className="text-brand font-medium hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
